
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import VideoCardWrapper from '@/components/layout/VideoCardWrapper';
import { Video } from '@/lib/api';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface FavoritesListProps {
  videos: Video[]; // Gardé pour la compatibilité mais non utilisé
  favorites: string[];
  isLoading: boolean;
  onRemoveFavorite: (videoId: string) => void;
}

const FavoritesList = ({ favorites, isLoading, onRemoveFavorite }: FavoritesListProps) => {
  const [favoriteVideos, setFavoriteVideos] = useState<Video[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchFavoriteVideos = async () => {
      if (!user || favorites.length === 0) {
        setFavoriteVideos([]);
        setLoadingVideos(false);
        return;
      }

      try {
        setLoadingVideos(true);
        // Charger les vidéos favorites depuis user_videos
        const { data, error } = await supabase
          .from('user_videos')
          .select('*')
          .eq('added_by_user_id', user.id)
          .in('video_id', favorites);

        if (error) throw error;

        // Convertir les données en format Video
        const videos: Video[] = data.map(item => ({
          id: item.video_id,
          title: item.title,
          thumbnail: item.thumbnail,
          channelName: item.channel_name,
          channelAvatar: item.channel_avatar,
          channelId: item.channel_id,
          views: item.views,
          published: item.published,
          publishedDate: item.published_date,
          duration: item.duration,
        }));

        setFavoriteVideos(videos);
      } catch (error) {
        console.error('Error fetching favorite videos:', error);
        setFavoriteVideos([]);
      } finally {
        setLoadingVideos(false);
      }
    };

    fetchFavoriteVideos();
  }, [user, favorites]);
  
  if (isLoading || loadingVideos) {
    return <Loader size="large" />;
  }
  
  if (favoriteVideos.length === 0) {
    return (
      <div className="text-center py-16 bg-white dark:bg-youtube-dark shadow-sm rounded-lg border border-gray-200 dark:border-gray-800">
        <h2 className="text-xl font-medium mb-4">Pas de vidéos favorites</h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          Vous n'avez pas encore ajouté de vidéos à vos favoris. Parcourez les vidéos et cliquez sur l'icône ❤️ pour les ajouter ici.
        </p>
      </div>
    );
  }
  
  return (
    <div className="video-grid">
      {favoriteVideos.map((video) => (
        <VideoCardWrapper
          key={video.id}
          {...video}
        />
      ))}
    </div>
  );
};

export default FavoritesList;
